import type {
  AnalysisObjectTiming,
  AnalysisObjectTimings,
  AnalysisStoredObjectTimings,
  QlikEvaluationPayload,
  QlikEvaluationSheet,
} from "@repo/shared";
import type { TFunction } from "i18next";
import { ANALYSIS_MEASUREMENT_PHASES } from "@repo/shared";

// -----------------------------------------------------------------------------
// Reading Qlik's evaluation payload for the UI.
//
// The one rule this module exists to enforce: Qlik covers the FULL SHEET plus
// only the top-5 slowest objects of each sheet (35 of 236 on the probed app).
// An object outside that slice has NO time — never zero, never fast. So the
// index below only ever contains what Qlik actually reported (the sheets among
// them, flagged), and a missing entry is the caller's cue to render "not
// covered".
// -----------------------------------------------------------------------------

/** Everything Qlik reported about one object, gathered from its many lists. */
export type QlikObjectCoverage = {
  /** Cold response time in seconds (sheetsUncached / objSlowUncached). */
  uncachedSeconds?: number;
  /** Warm response time in seconds (sheetsCached / objSlowCached). */
  cachedSeconds?: number;
  /** e.g. CALC-TIMEOUT. */
  timeoutStatusCode?: string;
  /** e.g. OUT-OF-MEMORY (objMemoryLimit). */
  memoryLimitStatusCode?: string;
  /** objSingleThreaded: share of one core the calculation stayed on. */
  cpuQuotient1?: number;
  /** objNoCache: the engine could not cache this object's result. */
  noCache?: boolean;
  /** A `notevaluated` event (e.g. INFO-CALC-COND) naming this object. */
  notEvaluatedCode?: string;
  notEvaluatedMessage?: string;
  /**
   * This id is a SHEET, not an object: the time is the sheet's own batch time —
   * the one thing Qlik measures well — and must be labelled as such.
   */
  isSheet?: boolean;
};

function entryFor(
  index: Map<string, QlikObjectCoverage>,
  objectId: string,
): QlikObjectCoverage | null {
  if (!objectId) return null;
  const existing = index.get(objectId);
  if (existing) return existing;
  const created: QlikObjectCoverage = {};
  index.set(objectId, created);
  return created;
}

/**
 * Object id → what Qlik measured for it. Keyed by `objectspec.id`, which is the
 * object's qId — the same key as `AnalysisObject.id`, so the join is direct.
 * An id absent from the map was not covered by the evaluation.
 */
export function buildQlikObjectIndex(
  payload: QlikEvaluationPayload | null | undefined,
): Map<string, QlikObjectCoverage> {
  const index = new Map<string, QlikObjectCoverage>();
  const result = payload?.result;
  if (!result) return index;

  const addTime = (
    objectId: string,
    key: "uncachedSeconds" | "cachedSeconds",
    seconds: number,
    timeoutStatusCode?: string,
  ) => {
    const entry = entryFor(index, objectId);
    if (!entry) return;
    // The same object can appear in a sheet's top-5 and in the global top-5;
    // keep the worst reading rather than whichever list came last.
    entry[key] = Math.max(entry[key] ?? 0, seconds);
    if (timeoutStatusCode) entry.timeoutStatusCode = timeoutStatusCode;
  };

  // The sheets themselves are rows in `analysis.objects` too, and Qlik's sheet
  // reading is its most trustworthy number — index it, flagged, instead of
  // telling a sheet row it was "not covered" while the panel above shows 3.69 s.
  const addSheetTime = (
    sheet: QlikEvaluationSheet,
    key: "uncachedSeconds" | "cachedSeconds",
  ): void => {
    const { id, responseTimeSeconds, timeoutStatusCode } = sheet.sheet;
    addTime(id, key, responseTimeSeconds, timeoutStatusCode);
    const entry = index.get(id);
    if (entry) entry.isSheet = true;
  };

  for (const sheet of result.sheetsUncached) {
    addSheetTime(sheet, "uncachedSeconds");
    for (const o of sheet.sheetObjects) {
      addTime(o.id, "uncachedSeconds", o.responseTimeSeconds, o.timeoutStatusCode);
    }
  }
  for (const sheet of result.sheetsCached) {
    addSheetTime(sheet, "cachedSeconds");
    for (const o of sheet.sheetObjects) {
      addTime(o.id, "cachedSeconds", o.responseTimeSeconds, o.timeoutStatusCode);
    }
  }
  for (const o of result.objSlowUncached) {
    addTime(o.id, "uncachedSeconds", o.responseTimeSeconds, o.timeoutStatusCode);
  }
  for (const o of result.objSlowCached) {
    addTime(o.id, "cachedSeconds", o.responseTimeSeconds, o.timeoutStatusCode);
  }
  for (const o of result.objNoCache) {
    const entry = entryFor(index, o.id);
    if (entry) entry.noCache = true;
  }
  for (const o of result.objMemoryLimit) {
    const entry = entryFor(index, o.id);
    if (entry) entry.memoryLimitStatusCode = o.memoryLimitStatusCode || "OUT-OF-MEMORY";
  }
  for (const o of result.objSingleThreaded) {
    const entry = entryFor(index, o.id);
    if (entry) entry.cpuQuotient1 = o.cpuQuotient1;
  }
  // `finished` does not mean "everything was evaluated": the probed app finished
  // carrying 17 of these. An object named here has no time on purpose.
  for (const event of payload?.events ?? []) {
    if (event.severity !== "notevaluated") continue;
    const entry = entryFor(index, event.objectId);
    if (!entry) continue;
    entry.notEvaluatedCode = event.errorCode;
    entry.notEvaluatedMessage = event.message;
  }

  return index;
}

/** One published sheet, cold and cached side by side. */
export type QlikSheetRow = {
  sheetId: string;
  title: string;
  objectCount: number;
  uncachedSeconds?: number;
  cachedSeconds?: number;
  /** How many of the sheet's objects Qlik reported a time for (its top 5). */
  coveredObjects: number;
};

function upsertSheet(
  rows: Map<string, QlikSheetRow>,
  sheet: QlikEvaluationSheet,
  key: "uncachedSeconds" | "cachedSeconds",
): void {
  const id = sheet.sheet.id;
  if (!id) return;
  const row = rows.get(id) ?? {
    sheetId: id,
    title: sheet.sheet.title || id,
    objectCount: sheet.objectCount,
    coveredObjects: 0,
  };
  row[key] = sheet.sheet.responseTimeSeconds;
  row.objectCount = Math.max(row.objectCount, sheet.objectCount);
  row.coveredObjects = Math.max(row.coveredObjects, sheet.sheetObjects.length);
  rows.set(id, row);
}

/** Sheet-level block: the measurement Qlik actually does well. Slowest first. */
export function buildQlikSheetRows(payload: QlikEvaluationPayload | null | undefined) {
  const rows = new Map<string, QlikSheetRow>();
  const result = payload?.result;
  if (!result) return [];
  for (const sheet of result.sheetsUncached) upsertSheet(rows, sheet, "uncachedSeconds");
  for (const sheet of result.sheetsCached) upsertSheet(rows, sheet, "cachedSeconds");
  return [...rows.values()].sort(
    (a, b) => (b.uncachedSeconds ?? 0) - (a.uncachedSeconds ?? 0) || a.title.localeCompare(b.title),
  );
}

/** Objects Qlik skipped — `finished` overstates coverage without this number. */
export function countNotEvaluated(payload: QlikEvaluationPayload | null | undefined): number {
  return (payload?.events ?? []).filter((e) => e.severity === "notevaluated").length;
}

/** Object id → our own timing, so the Objects table can look one up per row. */
export function buildObjectTimingIndex(
  timings: AnalysisObjectTimings | null | undefined,
): Map<string, AnalysisObjectTiming> {
  const index = new Map<string, AnalysisObjectTiming>();
  for (const timing of timings?.objects ?? []) index.set(timing.objectId, timing);
  return index;
}

/** A context without a result has no readings, even when its siblings do. */
export function getObjectTimingsForContext(
  timings: AnalysisStoredObjectTimings | null | undefined,
  order: number,
): AnalysisObjectTimings | null | undefined {
  return timings && "contexts" in timings
    ? timings.contexts.find((context) => context.order === order)?.result
    : timings;
}

export function formatMeasurementProgress(currentItem: string, t: TFunction<"analyses">): string {
  const match = /^Measuring context (\d+)\/(\d+): (.+)$/s.exec(currentItem);
  return match
    ? t("objectTimings.contextProgress", { order: match[1], total: match[2], name: match[3] })
    : currentItem;
}

const CONTEXT_LOG_MESSAGES: Record<string, string> = {
  "Measurement context started": "started",
  "Measurement context finished": "finished",
  "Measurement context not executed": "notExecuted",
};

export function formatMeasurementLogMessage(
  message: string,
  attrs: Record<string, unknown> | null,
  t: TFunction<"analyses">,
): string {
  const key = Object.hasOwn(CONTEXT_LOG_MESSAGES, message)
    ? CONTEXT_LOG_MESSAGES[message]
    : undefined;
  const label = key ? t(`objectTimings.contextLog.${key}`) : message;
  if (typeof attrs?.contextName !== "string") return label;
  return t("objectTimings.contextLog.message", {
    name: attrs.contextName,
    order: attrs.contextOrder,
    total: attrs.contextTotal,
    message: label,
  });
}

/**
 * Object readings and stage gaps answer different coverage questions. Objects
 * the app itself does not calculate (a condition not met, a definition the
 * engine rejects) carry no reading and are counted apart, never as gaps.
 */
export function summarizeObjectTimingCoverage(
  timings: AnalysisObjectTimings,
  requested = timings.measurementPhases ?? ANALYSIS_MEASUREMENT_PHASES,
  expectedObjectCount = timings.objects.length,
) {
  const withReason = (reason: AnalysisObjectTiming["reason"]) =>
    timings.objects.filter((o) => o.status === "notMeasured" && o.reason === reason).length;
  return {
    completedObjects: timings.objects.filter((o) => o.status === "cheap" || o.status === "signal")
      .length,
    blockedObjects: withReason("calcCondition"),
    notCalculableObjects: withReason("notCalculable"),
    totalObjects: Math.max(expectedObjectCount, timings.objects.length),
    omittedPhases: ANALYSIS_MEASUREMENT_PHASES.filter((phase) => !requested.includes(phase)),
    incompletePhases: ANALYSIS_MEASUREMENT_PHASES.flatMap((phase) => {
      const reason = timings.partialByPhase?.[phase];
      return requested.includes(phase) && reason ? [{ phase, reason }] : [];
    }),
  };
}

// --- Number formatting (compact, mono-friendly, no thousands noise) ---

/** Qlik reports seconds; under a second, milliseconds read better. */
export function formatQlikSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0 ms";
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  return `${seconds.toFixed(2)} s`;
}

/** Our own measurements are milliseconds above the RTT floor. */
export function formatTimingMs(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  if (Math.abs(ms) < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/** A timeout is a lower bound; rounding up would overstate what was observed. */
export function formatTimingLowerBound(ms: number): string {
  return `≥ ${(Math.floor(ms / 10) / 100).toFixed(2)} s`;
}

export function formatMiB(mib: number): string {
  if (!Number.isFinite(mib) || mib <= 0) return "0 MiB";
  if (mib >= 1024) return `${(mib / 1024).toFixed(1)} GiB`;
  return `${mib.toFixed(0)} MiB`;
}
